import { Button } from "../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../components/ui/card";
import {
	CheckCircle,
	Droplets,
	Wrench,
	Building2,
	ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";

// Utility function for scroll to top
function scrollToTop(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}

export const Services = () => (
	<section id="services" className="py-16 relative w-full flex justify-center">
		<div
			className="absolute inset-0 pointer-events-none"
			aria-hidden="true"
		></div>
		<div className="relative w-full max-w-7xl 2xl:max-w-[1800px] 3xl:max-w-[2100px] mx-auto px-2 md:px-0">
			<header className="text-center mb-16">
				<h2 className="text-4xl md:text-5xl mb-4 font-extrabold text-primary tracking-tight leading-tight">
					Central Texas Engineering Services
				</h2>
				<p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
					GOVA Engineering, LLC provides comprehensive Civil Engineering
					Services for land development projects that include subdivisions and
					site plans for residential and commercial projects. Our licensed
					engineers deliver results that meet local regulations and exceed
					client expectations.
				</p>
			</header>
			<div className="grid md:grid-cols-3 xl:grid-cols-3 gap-10 mx-auto max-w-full">
				{/* Civil Engineering Card */}
				<Card className="group border-none bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col rounded-2xl">
					<CardHeader className="pb-2 flex flex-col items-center">
						<Wrench
							className="h-10 w-10 text-primary mb-2"
							aria-hidden="true"
						/>
						<CardTitle className="font-bold text-2xl mb-1">
							Civil Engineering
						</CardTitle>
						<CardDescription className="text-base text-muted-foreground">
							Site development, grading, drainage, and infrastructure design
						</CardDescription>
					</CardHeader>
					<CardContent className="flex-1 flex flex-col justify-between">
						<ul className="space-y-2 mb-6 text-base">
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> Site
								Planning & Design
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> Grading
								and Drainage Solutions
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" />{" "}
								Detention & Water Quality Solutions
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" />{" "}
								Floodplains Studies
							</li>
						</ul>
						<Button
							asChild
							className="w-full font-semibold text-base mt-auto"
							variant="default"
							size="lg"
						>
							<Link to="/civil-engineering" onClick={scrollToTop}>
								Learn More <ArrowRight className="h-4 w-4 ml-2" />
							</Link>
						</Button>
					</CardContent>
				</Card>
				{/* Water & Wastewater Card */}
				<Card className="group border-none bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col rounded-2xl">
					<CardHeader className="pb-2 flex flex-col items-center">
						<Droplets
							className="h-10 w-10 text-primary mb-2"
							aria-hidden="true"
						/>
						<CardTitle className="font-bold text-2xl mb-1">
							Water & Wastewater
						</CardTitle>
						<CardDescription className="text-base text-muted-foreground">
							Utility tap plans, water line sizing, and coordination
						</CardDescription>
					</CardHeader>
					<CardContent className="flex-1 flex flex-col justify-between">
						<ul className="space-y-2 mb-6 text-base">
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> City of
								Austin Tap Plan Permits
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> City of
								Austin SER Applications
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> Private
								and Public Wastewater Collection Systems
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> Private
								and Public Water Distribution Systems
							</li>
						</ul>
						<Button
							asChild
							className="w-full font-semibold text-base mt-auto"
							variant="default"
							size="lg"
						>
							<Link to="/water-wastewater" onClick={scrollToTop}>
								Learn More <ArrowRight className="h-4 w-4 ml-2" />
							</Link>
						</Button>
					</CardContent>
				</Card>
				{/* Permitting & Compliance Card */}
				<Card className="group border-none bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col rounded-2xl">
					<CardHeader className="pb-2 flex flex-col items-center">
						<Building2
							className="h-10 w-10 text-primary mb-2"
							aria-hidden="true"
						/>
						<CardTitle className="font-bold text-2xl mb-1">
							Permitting & Compliance
						</CardTitle>
						<CardDescription className="text-base text-muted-foreground">
							Local, county, and environmental approvals
						</CardDescription>
					</CardHeader>
					<CardContent className="flex-1 flex flex-col justify-between">
						<ul className="space-y-2 mb-6 text-base">
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary flex-shrink-0" />{" "}
								City of Austin Site Plan and Subdivision Permits
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> TCEQ -
								WPAP & SCS Permits
							</li>
							<li className="flex items-center gap-2">
								<CheckCircle className="h-4 w-4 min-w-4 text-primary" /> Zoning
								Requirements
							</li>
						</ul>
						<Button
							asChild
							className="w-full font-semibold text-base mt-auto"
							variant="default"
							size="lg"
						>
							<Link to="/permitting" onClick={scrollToTop}>
								Learn More <ArrowRight className="h-4 w-4 ml-2" />
							</Link>
						</Button>
					</CardContent>
				</Card>
				{/* Project Management Card
		<Card className="group border-none bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 h-full flex flex-col rounded-2xl">
		  <CardHeader className="pb-2 flex flex-col items-center">
			<Users className="h-10 w-10 text-primary mb-2" aria-hidden="true" />
			<CardTitle className="font-bold text-2xl mb-1">Project Management</CardTitle>
			<CardDescription className="text-base text-muted-foreground">End-to-end project coordination and oversight</CardDescription>
		  </CardHeader>
		  <CardContent className="flex-1 flex flex-col justify-between">
			<ul className="space-y-2 mb-6 text-base">
			  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> Timeline management</li>
			  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> Stakeholder coordination</li>
			  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> Quality assurance</li>
			  <li className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> Cost optimization</li>
			</ul>
			<Button asChild className="w-full font-semibold text-base mt-auto" variant="default" size="lg">
			  <Link to="/project-management" onClick={scrollToTop}>Learn More <ArrowRight className="h-4 w-4 ml-2" /></Link>
			</Button>
		  </CardContent>
		</Card> */}
			</div>
			{/* SEO/Lead CTA Section */}
			<div className="mt-20 text-center max-w-2xl mx-auto">
				<h3 className="text-2xl md:text-3xl font-bold mb-4 text-primary">
					Ready to Start Your Project?
				</h3>
				<p className="mb-6 text-lg text-muted-foreground">
					Contact our team for a free consultation and discover how Gova
					Engineering can help you achieve your development goals in Central
					Texas.
				</p>
				<Button
					asChild
					size="lg"
					className="text-lg px-10 py-5 font-bold shadow-xl"
				>
					<a href="#contact">Request a Free Consultation</a>
				</Button>
			</div>
		</div>
	</section>
);
